import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { CalculatorInput, CalculatorResult } from '../types';

const ROICalculator = () => {
  const [inputs, setInputs] = useState<CalculatorInput>({
    monthlyTasks: 100,
    timePerTask: 30,
    hourlyRate: 500,
    employeeCount: 5,
  });

  const [results, setResults] = useState<CalculatorResult | null>(null);

  const calculateROI = () => {
    const hoursPerMonth = (inputs.monthlyTasks * inputs.timePerTask * inputs.employeeCount) / 60;
    const costPerMonth = hoursPerMonth * inputs.hourlyRate;
    const annualSavings = costPerMonth * 12;
    const automationCost = 50000;
    const roi = ((annualSavings - automationCost) / automationCost) * 100;
    const breakEvenMonths = automationCost / costPerMonth;

    setResults({
      hoursPerMonth,
      costPerMonth,
      annualSavings,
      roi,
      breakEvenMonths,
    });
  };

  const handleInputChange = (field: keyof CalculatorInput, value: string) => {
    setInputs((prev) => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
  };

  return (
    <div className="bg-card rounded-xl border border-border p-8">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-text-primary mb-2">ROI Calculator</h3>
        <p className="text-text-secondary">
          Calculate your potential savings with automation. Adjust the values below to see your
          personalized ROI projection.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Input
          name="monthlyTasks"
          autoComplete="off"
          label="Monthly Repetitive Tasks"
          type="number"
          value={inputs.monthlyTasks}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('monthlyTasks', e.target.value)}
          description="Number of tasks performed each month"
        />
        <Input
          name="timePerTask"
          autoComplete="off"
          label="Average Time per Task (minutes)"
          type="number"
          value={inputs.timePerTask}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('timePerTask', e.target.value)}
          description="Time spent on each task"
        />
        <Input
          name="hourlyRate"
          autoComplete="off"
          label="Hourly Rate (₹)"
          type="number"
          value={inputs.hourlyRate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('hourlyRate', e.target.value)}
          description="Average hourly cost per employee"
        />
        <Input
          name="employeeCount"
          autoComplete="off"
          label="Number of Employees"
          type="number"
          value={inputs.employeeCount}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('employeeCount', e.target.value)}
          description="Team members performing these tasks"
        />
      </div>

      <Button variant="default" size="lg" fullWidth onClick={calculateROI}>
        Calculate Savings
      </Button>

      {results && (
        <div className="mt-8 pt-8 border-t border-border">
          <h4 className="text-xl font-bold text-text-primary mb-6">Your Potential Savings</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-surface rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Clock" size={20} className="text-primary" />
                <span className="text-sm text-text-secondary">Hours Saved Monthly</span>
              </div>
              <div className="text-3xl font-bold text-text-primary">
                {results.hoursPerMonth.toFixed(0)}
              </div>
            </div>

            <div className="bg-surface rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={20} className="text-success" />
                <span className="text-sm text-text-secondary">Monthly Cost Savings</span>
              </div>
              <div className="text-3xl font-bold text-success">
                ₹{results.costPerMonth.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
            </div>

            <div className="bg-surface rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={20} className="text-accent" />
                <span className="text-sm text-text-secondary">Annual Savings</span>
              </div>
              <div className="text-3xl font-bold text-accent">
                ₹{results.annualSavings.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
            </div>

            <div className="bg-surface rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Target" size={20} className="text-primary" />
                <span className="text-sm text-text-secondary">ROI Percentage</span>
              </div>
              <div className="text-3xl font-bold text-primary">{results.roi.toFixed(0)}%</div>
            </div>

            <div className="bg-surface rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Calendar" size={20} className="text-text-secondary" />
                <span className="text-sm text-text-secondary">Break-Even Period</span>
              </div>
              <div className="text-3xl font-bold text-text-primary">
                {results.breakEvenMonths.toFixed(1)} mo
              </div>
            </div>

            <div className="bg-primary/10 rounded-lg p-4 border border-primary">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Zap" size={20} className="text-primary" />
                <span className="text-sm text-primary font-semibold">Recommended Plan</span>
              </div>
              <div className="text-2xl font-bold text-primary">
                {results.annualSavings > 50000 ? 'Enterprise' : results.annualSavings > 20000 ? 'Professional' : 'Starter'}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-accent/10 rounded-lg p-4 border border-accent">
            <p className="text-sm text-text-primary">
              <strong>Note:</strong> These calculations are estimates based on your inputs. Actual
              savings may vary depending on implementation complexity and specific use cases. Book a
              free audit for a detailed analysis.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ROICalculator;